import { REGION_CITY_ROWS } from '../data/regionsCatalog';
import { getCityNativeLabel } from '../data/onboardingRegionLabels';

/** ISO → 다국어 국가명 (검색·저장 라벨 파싱용) */
const COUNTRY_DISPLAY = {
  VN: { ko: '베트남', en: 'Vietnam', vi: 'Việt Nam', ru: 'Вьетнам' },
  KR: { ko: '대한민국', en: 'South Korea', vi: 'Hàn Quốc', ru: 'Южная Корея' },
  JP: { ko: '일본', en: 'Japan', vi: 'Nhật Bản', ru: 'Япония' },
  TH: { ko: '태국', en: 'Thailand', vi: 'Thái Lan', ru: 'Таиланд' },
  PH: { ko: '필리핀', en: 'Philippines', vi: 'Philippines', ru: 'Филиппины' },
  ID: { ko: '인도네시아', en: 'Indonesia', vi: 'Indonesia', ru: 'Индонезия' },
  CN: { ko: '중국', en: 'China', vi: 'Trung Quốc', ru: 'Китай' },
  TW: { ko: '대만', en: 'Taiwan', vi: 'Đài Loan', ru: 'Тайвань' },
  US: { ko: '미국', en: 'United States', vi: 'Hoa Kỳ', ru: 'США' },
  GB: { ko: '영국', en: 'United Kingdom', vi: 'United Kingdom', ru: 'Великобритания' },
  RU: { ko: '러시아', en: 'Russia', vi: 'Nga', ru: 'Россия' },
  IN: { ko: '인도', en: 'India', vi: 'Ấn Độ', ru: 'Индия' },
  SG: { ko: '싱가포르', en: 'Singapore', vi: 'Singapore', ru: 'Сингапур' },
  MY: { ko: '말레이시아', en: 'Malaysia', vi: 'Malaysia', ru: 'Малайзия' },
  AU: { ko: '호주', en: 'Australia', vi: 'Australia', ru: 'Австралия' },
};

function pickLang(lang) {
  if (lang === 'en' || lang === 'vi' || lang === 'ru') return lang;
  return 'ko';
}

function countryLabel(code, lang) {
  const cc = String(code || '').toUpperCase();
  const row = COUNTRY_DISPLAY[cc];
  if (!row) return '';
  return row[pickLang(lang)] || row.en || row.ko;
}

function localizeCountryPart(part, lang) {
  const p = String(part || '').trim();
  if (!p) return '';
  for (const [cc, labels] of Object.entries(COUNTRY_DISPLAY)) {
    const variants = [labels.ko, labels.en, labels.vi, labels.ru].filter(Boolean);
    if (variants.some((v) => v.toLowerCase() === p.toLowerCase())) {
      return countryLabel(cc, lang) || p;
    }
  }
  return p;
}

function fieldByLang(row, lang, koKey, enKey, localKey, ruKey) {
  if (!row) return '';
  const l = pickLang(lang);
  if (l === 'ko') return row[koKey] || row[enKey] || row[localKey] || '';
  if (l === 'vi') return row[localKey] || row[enKey] || row[koKey] || '';
  if (l === 'ru' && ruKey) return row[ruKey] || row[enKey] || row[koKey] || row[localKey] || '';
  return row[enKey] || row[koKey] || row[localKey] || '';
}

/**
 * locations / district 검색 row → 표시 라벨 (앱 언어 기준)
 * @param {object} row
 * @param {string} [language]
 * @param {'city'|'district'} [mode]
 */
export function formatLocationDistrictLabel(row, language = 'ko', mode = 'city') {
  if (!row) return '';
  const lang = pickLang(language);
  const cc = row.country_code || '';
  const country =
    fieldByLang(row, lang, 'country_name_ko', 'country_name_en', 'country_name_vi', 'country_name_ru') ||
    countryLabel(cc, lang);
  const city = fieldByLang(row, lang, 'city_ko', 'city_en', 'city_vi', 'city_ru');
  const district = fieldByLang(row, lang, 'district_ko', 'district_en', 'district_vi', 'district_ru');
  const cityNorm = city.trim().toLowerCase();
  const distNorm = district.trim().toLowerCase();
  const hasDistinctDistrict = Boolean(district && city && distNorm !== cityNorm);

  if (mode === 'city' || !hasDistinctDistrict) {
    return `${country} · ${city}`;
  }
  return `${country} · ${city} · ${district}`;
}

/**
 * @param {object} location — locations 테이블 row
 * @param {string} [language]
 */
export function formatLocationRowLabel(location, language = 'ko') {
  if (!location) return '';
  const lang = pickLang(language);
  if (location.level === 'district') {
    return formatLocationDistrictLabel(location, lang, 'district');
  }
  if (location.name_ko || location.name_en || location.name_local) {
    if (lang === 'ko') return location.name_ko || location.name_en || location.name_local || '';
    if (lang === 'vi') return location.name_local ?? location.name_en ?? location.name_ko ?? '';
    if (lang === 'ru') return location.name_ru || location.name_en || location.name_ko || location.name_local || '';
    return location.name_en || location.name_ko || location.name_local || '';
  }
  return formatLocationDistrictLabel(location, lang, 'city');
}

/**
 * localStorage·selectedRegions 저장 문자열 → 표시 라벨
 * @param {string} value
 * @param {(key: string) => string} t
 * @param {string} [language]
 */
export function formatStoredRegionLabel(value, t, language = 'ko') {
  if (value == null || value === '') return '';
  const v = String(value).trim();
  const lang = pickLang(language);

  if (v.includes('·')) {
    return v
      .split('·')
      .map((part) => formatStoredRegionLabel(part.trim(), t, language))
      .join(' · ');
  }

  const localizedCountry = localizeCountryPart(v, lang);
  if (localizedCountry !== v) return localizedCountry;

  const row = REGION_CITY_ROWS.find((r) => r.pick === v || r.ko === v);
  if (row) {
    const regionKey = `regions.${row.key}`;
    const translated = t?.(regionKey);
    if (translated && translated !== regionKey) return translated;
    if (lang === 'ko') return row.ko;
    if (lang === 'vi' && row.country === 'VN') return getCityNativeLabel(row) || row.pick;
    return row.pick;
  }

  return v;
}
